// QRModal.js - Updated with scenario-specific flow
import React, { useState } from 'react';
import { X, ExternalLink, CheckCircle } from 'lucide-react';

const QRModal = ({ 
  isOpen, 
  onClose, 
  onSurveyComplete,
  currentScenario,
  onContinueScenario2,
  onSubmitFinal
}) => {
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const [showSurveyLink, setShowSurveyLink] = useState(false);

  if (!isOpen) return null;

  const handleQRScan = () => {
    setShowSurveyLink(true);
    // Simulate opening survey
    setTimeout(() => {
      setSurveyCompleted(true);
    }, 2000); // Simulate survey completion after 2 seconds
  };

  const handleFinish = () => {
    if (onSurveyComplete) {
      onSurveyComplete();
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative">
        {/* Close button - only show if survey not completed */}
        {!surveyCompleted && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X size={24} />
          </button>
        )}

        {/* Content */}
        <div className="text-center">
          {!surveyCompleted ? (
            <>
              <h2 className="text-2xl font-bold text-green-600 mb-6">
                THIS IS THE END OF THE SCENARIO. WE HOPE YOU HAVE HAD AN INTERESTING EXPERIENCE.
              </h2>
              
              <h3 className="text-lg font-semibold text-blue-600 mb-4">
                SURVEY ON THE TEST AND LEARNING EXPERIENCE 
                <span className="text-sm text-gray-600 block">(not counted in test time)</span>
              </h3>
              
              <p className="text-gray-700 mb-6">
                Before you leave, please help us improve future test administration by following this link to evaluate your scenario-based test experience and your learning experience in the test.
              </p>

              {/* QR Code */}
              <div className="flex justify-center mb-6">
                <div className="border-4 border-blue-200 rounded-lg p-4 bg-white">
                  <img 
                    src="/QR.png" 
                    alt="QR Code for survey"
                    className="w-48 h-48 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'block';
                    }}
                  />
                  {/* Fallback if QR image not found */}
                  <div className="hidden w-48 h-48 bg-gray-100 border-2 border-dashed border-gray-300 rounded flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-4xl mb-2">📱</div>
                      <p className="text-gray-600 text-sm">QR Code</p>
                      <p className="text-gray-500 text-xs">Survey Link</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Survey link section */}
              {showSurveyLink && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800 text-sm mb-2">📋 Survey opened! Please complete it and return here.</p>
                  <div className="flex items-center justify-center text-blue-600 text-sm">
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Survey in progress...
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-4 justify-center">
                {!showSurveyLink ? (
                  <>
                    <button
                      onClick={onClose}
                      className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      Skip Survey
                    </button>
                    <button
                      className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      onClick={handleQRScan}
                    >
                      Take Survey
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setSurveyCompleted(true)}
                    className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                  >
                    I've Completed the Survey
                  </button>
                )}
              </div>
            </>
          ) : (
            /* Survey completed state */
            <div className="space-y-6">
              <div className="flex items-center justify-center mb-4">
                <CheckCircle className="w-16 h-16 text-green-500" />
              </div>
              
              <h2 className="text-2xl font-bold text-green-600 mb-4">
                THANK YOU!
              </h2>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-green-800 mb-2">✅ Test & Survey Completed</h3>
                <p className="text-green-700 text-sm">
                  Your responses have been recorded. Thank you for participating in this scenario-based EAP test experience.
                </p>
              </div>

              {currentScenario === 1 ? (
                <button
                  onClick={onContinueScenario2}
                  className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Continue
                </button>
              ) : (
                <button
                  onClick={onSubmitFinal}
                  className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  Submit Test
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRModal;
